// ============================================================
// interactionMachine — カレンダーDnDの純粋 state machine
//
// 旧 useCalendarInteraction は dragRef / resizeRef / headerDragRef の
// 3つの独立 ref が mouseup ハンドラ内の早期 return の「順序」で
// 整合性を保っており、リセット漏れ → ゴースト残留 → 誤予約作成の
// バグクラスを繰り返し生んだ（2026-05-31 / 2026-06-20 修正）。
//
// この machine は単一の discriminated union 状態 + 純粋 reducer で、
// **どの状態から 'up' / 'cancel' を受けても必ず idle に戻る**ことを
// 型と網羅 switch で構造的に保証する（テスト: interactionMachine.test.ts）。
//
// 座標変換・ヒットテスト・日付変換は行わない（呼び出し側 = hook の責務）。
// 入力は正規化済みイベント、出力は次状態 + 宣言的な effect のリスト。
//
// ジェスチャ仕様: md/master_md/page-calendar.md「DnDジェスチャ仕様」
// ============================================================

import { Reservation } from './types';

/** ドラッグ新規作成の発火閾値（|dx|+|dy| px） */
export const DRAG_CREATE_THRESHOLD_PX = 8;
/** リサイズの発火閾値（|dx|+|dy| px） */
export const RESIZE_THRESHOLD_PX = 5;
/** ヘッダー列ドラッグの発火閾値（|dx| px、横方向のみ） */
export const HEADER_DRAG_THRESHOLD_PX = 8;

/** pointerdown 時に呼び出し側がヒットテストで決定する「押下対象」 */
export type PressTarget =
  | { type: 'headerDrag'; fromIndex: number }
  | {
      type: 'resize';
      reservation: Reservation;
      edge: 'top' | 'bottom';
      origAbsTop: number;
      origAbsBottom: number;
      vehicleIndex: number;
    }
  | { type: 'dragCreate'; vehicleIndex: number; startAbsY: number }
  /** 予約ブロック中央 / ガター / 移動モード中など、離した時にクリック解決するだけの押下 */
  | { type: 'click'; onHeader: boolean };

export type MachineState =
  | { kind: 'idle' }
  /** 押下済み・閾値未満（ジェスチャ未確定） */
  | { kind: 'pressed'; downX: number; downY: number; target: PressTarget }
  | { kind: 'headerDrag'; fromIndex: number }
  | {
      kind: 'resize';
      reservation: Reservation;
      edge: 'top' | 'bottom';
      origAbsTop: number;
      origAbsBottom: number;
      vehicleIndex: number;
      lastBodyY: number;
    }
  | { kind: 'dragCreate'; vehicleIndex: number; startAbsY: number; lastBodyY: number };

export const IDLE: MachineState = { kind: 'idle' };

export type MachineEvent =
  | { type: 'down'; clientX: number; clientY: number; target: PressTarget }
  | { type: 'move'; clientX: number; clientY: number; bodyY: number; headerCol: number }
  /** bodyY が null の場合はコンテナ外リリース → 状態が持つ lastBodyY を使う */
  | { type: 'up'; bodyY: number | null; headerCol: number }
  | { type: 'cancel' };

export type MachineEffect =
  | { type: 'dragGhost'; vehicleIndex: number; startAbsY: number; currentAbsY: number }
  | {
      type: 'resizeGhost';
      reservation: Reservation;
      edge: 'top' | 'bottom';
      origAbsTop: number;
      origAbsBottom: number;
      vehicleIndex: number;
      bodyY: number;
    }
  | { type: 'clearGhosts' }
  | { type: 'headerDragStart'; fromIndex: number }
  | { type: 'headerDragOver'; targetCol: number }
  | { type: 'headerDrop'; fromIndex: number; toCol: number }
  | { type: 'headerDragEnd' }
  | {
      type: 'commitResize';
      reservation: Reservation;
      edge: 'top' | 'bottom';
      origAbsTop: number;
      origAbsBottom: number;
      bodyY: number;
    }
  | { type: 'commitDragCreate'; vehicleIndex: number; startAbsY: number; endAbsY: number }
  | { type: 'resolveClick'; onHeader: boolean };

export interface TransitionResult {
  next: MachineState;
  effects: MachineEffect[];
}

/**
 * 純粋 reducer。副作用なし・入力を変異しない。
 * 不変条件: event.type が 'up' | 'cancel' なら next は必ず idle。
 */
export function transition(state: MachineState, event: MachineEvent): TransitionResult {
  switch (state.kind) {
    case 'idle': {
      if (event.type === 'down') {
        return { next: { kind: 'pressed', downX: event.clientX, downY: event.clientY, target: event.target }, effects: [] };
      }
      // idle 中の move / up / cancel は何もしない（押下していないので click も発火しない）
      return { next: IDLE, effects: [] };
    }

    case 'pressed': {
      switch (event.type) {
        case 'move': {
          const t = state.target;
          if (t.type === 'headerDrag') {
            // 横方向のみで判定（旧実装と同一）
            if (Math.abs(event.clientX - state.downX) > HEADER_DRAG_THRESHOLD_PX) {
              return {
                next: { kind: 'headerDrag', fromIndex: t.fromIndex },
                effects: [
                  { type: 'headerDragStart', fromIndex: t.fromIndex },
                  { type: 'headerDragOver', targetCol: event.headerCol },
                ],
              };
            }
            return { next: state, effects: [] };
          }
          if (t.type === 'resize') {
            const dist = Math.abs(event.clientX - state.downX) + Math.abs(event.clientY - state.downY);
            if (dist < RESIZE_THRESHOLD_PX) return { next: state, effects: [] };
            return {
              next: { kind: 'resize', reservation: t.reservation, edge: t.edge, origAbsTop: t.origAbsTop, origAbsBottom: t.origAbsBottom, vehicleIndex: t.vehicleIndex, lastBodyY: event.bodyY },
              effects: [
                { type: 'resizeGhost', reservation: t.reservation, edge: t.edge, origAbsTop: t.origAbsTop, origAbsBottom: t.origAbsBottom, vehicleIndex: t.vehicleIndex, bodyY: event.bodyY },
              ],
            };
          }
          if (t.type === 'dragCreate') {
            const dist = Math.abs(event.clientX - state.downX) + Math.abs(event.clientY - state.downY);
            if (dist < DRAG_CREATE_THRESHOLD_PX) return { next: state, effects: [] };
            return {
              next: { kind: 'dragCreate', vehicleIndex: t.vehicleIndex, startAbsY: t.startAbsY, lastBodyY: event.bodyY },
              effects: [
                { type: 'dragGhost', vehicleIndex: t.vehicleIndex, startAbsY: t.startAbsY, currentAbsY: event.bodyY },
              ],
            };
          }
          // click ターゲットは移動してもジェスチャ化しない（旧実装と同一: 離した時にクリック解決）
          return { next: state, effects: [] };
        }
        case 'up': {
          const t = state.target;
          if (t.type === 'headerDrag') {
            // 閾値未満で離した = ヘッダークリック。旧実装は headerDragEnd → クリック解決の順
            return { next: IDLE, effects: [{ type: 'clearGhosts' }, { type: 'headerDragEnd' }, { type: 'resolveClick', onHeader: true }] };
          }
          const onHeader = t.type === 'click' ? t.onHeader : false;
          return { next: IDLE, effects: [{ type: 'clearGhosts' }, { type: 'resolveClick', onHeader }] };
        }
        case 'cancel':
          return { next: IDLE, effects: [{ type: 'clearGhosts' }] };
        case 'down':
          // 押下中の再押下（多重ポインタ等）→ 新しい押下で上書き
          return { next: { kind: 'pressed', downX: event.clientX, downY: event.clientY, target: event.target }, effects: [] };
      }
      return { next: state, effects: [] };
    }

    case 'headerDrag': {
      switch (event.type) {
        case 'move':
          return { next: state, effects: [{ type: 'headerDragOver', targetCol: event.headerCol }] };
        case 'up':
          return {
            next: IDLE,
            effects: [
              { type: 'clearGhosts' },
              { type: 'headerDrop', fromIndex: state.fromIndex, toCol: event.headerCol },
              { type: 'headerDragEnd' },
            ],
          };
        case 'cancel':
          return { next: IDLE, effects: [{ type: 'clearGhosts' }, { type: 'headerDragEnd' }] };
        case 'down':
          return { next: state, effects: [] };
      }
      return { next: state, effects: [] };
    }

    case 'resize': {
      switch (event.type) {
        case 'move':
          return {
            next: { ...state, lastBodyY: event.bodyY },
            effects: [
              { type: 'resizeGhost', reservation: state.reservation, edge: state.edge, origAbsTop: state.origAbsTop, origAbsBottom: state.origAbsBottom, vehicleIndex: state.vehicleIndex, bodyY: event.bodyY },
            ],
          };
        case 'up': {
          const bodyY = event.bodyY ?? state.lastBodyY;
          return {
            next: IDLE,
            effects: [
              { type: 'clearGhosts' },
              { type: 'commitResize', reservation: state.reservation, edge: state.edge, origAbsTop: state.origAbsTop, origAbsBottom: state.origAbsBottom, bodyY },
            ],
          };
        }
        case 'cancel':
          return { next: IDLE, effects: [{ type: 'clearGhosts' }] };
        case 'down':
          return { next: state, effects: [] };
      }
      return { next: state, effects: [] };
    }

    case 'dragCreate': {
      switch (event.type) {
        case 'move':
          return {
            next: { ...state, lastBodyY: event.bodyY },
            effects: [
              { type: 'dragGhost', vehicleIndex: state.vehicleIndex, startAbsY: state.startAbsY, currentAbsY: event.bodyY },
            ],
          };
        case 'up': {
          const endAbsY = event.bodyY ?? state.lastBodyY;
          return {
            next: IDLE,
            effects: [
              { type: 'clearGhosts' },
              { type: 'commitDragCreate', vehicleIndex: state.vehicleIndex, startAbsY: state.startAbsY, endAbsY },
            ],
          };
        }
        case 'cancel':
          return { next: IDLE, effects: [{ type: 'clearGhosts' }] };
        case 'down':
          return { next: state, effects: [] };
      }
      return { next: state, effects: [] };
    }
  }
}
